import moment from "moment";

import { toNumber } from "@common/lib/toNumber";
import { InnodomDateFormats } from "@common/util/dateFormats";
import {
  ATTR_CONFIDENCE,
  ATTR_ID,
  ATTR_ID_REF,
  ATTR_KEY,
  TAG_CONTENT,
  TAG_DATAPOINT,
  TAG_META,
} from "../constants";
import { stripNamespaceAttributes } from "../Document/InnodomContent/util";
import { ContentType, DataPointValue, State, TaxonomyEntry, TaxonomyEntryType } from "../types";
import { extractKeysAndIndexes } from "./getDefaultState";
import { WorkbenchTaxonomy } from "../../../../common/api/v1.1/types/taxonomy";

export const newDefaultValue = <T>(taxonomyKey: string, valueKey: string): DataPointValue<T> => ({
  confidence: 1.0,
  idRef: null,
  taxonomyKey : taxonomyKey.toLocaleLowerCase(),
  value: "" as any,
  valueKey : valueKey.toLocaleLowerCase(),
  valueRaw: "",
});
export const buildValueKey = (
  group: string | null,
  groupOrder: number | null,
  dataPoint: string,
  dataPointOrder: number | null,
): string => {
  let groupPart = "";
  if (group !== null) {
    groupPart = groupOrder === null ? `${group}.` : `${group}.${groupOrder}.`;
  }
  const dataPointPart = dataPointOrder === null ? dataPoint : `${dataPoint}.${dataPointOrder}`;
  return groupPart + dataPointPart;
};
export const getFirstKeyOfTaxonomyEntry = (
  taxonomyEntry: TaxonomyEntry,
  taxonomy: State["taxonomy"],
): string => {
  let group: string | null = null;
  let groupOrder: number | null = null;
  let dataPoint: string = taxonomyEntry.key;
  let dataPointOrder: number | null = null;
  if (taxonomyEntry.type === TaxonomyEntryType.Group) {
    group = taxonomyEntry.key;
    if (taxonomyEntry.multiple) {
      groupOrder = 0;
    }
    dataPoint = taxonomyEntry.dataPoints[0];
    if (taxonomy[dataPoint].multiple) {
      dataPointOrder = 0;
    }
  } else if (taxonomyEntry.multiple) {
    dataPointOrder = 0;
  }
  return buildValueKey(group, groupOrder, dataPoint, dataPointOrder);
};

export const isNonEmptyValue = (v: DataPointValue<any>): boolean =>
  v.idRef !== null || (v.value !== null && v.value !== "" && v.valueRaw !== "");

export const valueMissingIdRef = (values: State["values"], blocks: State["blocks"]): boolean => {
  for (const key of Object.keys(values)) {
    const idRef = values[key].idRef;

    // no valid idref means there's no support paragraph selected
    if (idRef === null || !(idRef in blocks)) {
      // prompt only if this data point has some value entered
      if (isNonEmptyValue(values[key])) {
        return true;
      }
    }
  }
  return false;
};

const unhandledType = (t: never): never => {
  throw new Error(`Unhandled taxonomy entry type: "${t}".`);
};

const falseValues = ["false", "no", "f", "n"];
const trueValues = ["true", "yes", "t", "y"];
const getBooleanFromString = (raw: string): [boolean, string] => {
  const split = raw
    .split(" ")
    .map(s => s.trim().toLowerCase())
    .filter(s => s.length > 0);
  if (split.length < 1) {
    return [false, "false"];
  }
  for (const word of split) {
    if (falseValues.includes(word)) {
      return [false, "false"];
    }
    if (trueValues.includes(word)) {
      return [true, "true"];
    }
  }
  throw new Error("Unable to extract boolean value.");
};

const getNumberFromString = (raw: string): [number, string] => {
  const wholeString = toNumber(raw);
  if (!isNaN(wholeString)) {
    return [wholeString, raw];
  }
  let num: number | undefined;
  let numString: string | undefined;
  const split = raw.split(" ");
  for (const word of split) {
    const piece = toNumber(word);
    if (isNaN(piece)) {
      if (num !== undefined) {
        return [num, raw];
      }
      continue;
    }
    if (num === undefined) {
      num = piece;
      numString = word;
      continue;
    }
    const testNumString = `${numString} ${word}`;
    const testNum = toNumber(testNumString);
    if (!isNaN(testNum)) {
      num = testNum;
      numString = testNumString;
      continue;
    }
    return [num, raw];
  }
  if (num === undefined) {
    throw new Error("Unable to extract number value.");
  }
  return [num, raw];
};

const isDateFormat = (key: string): key is keyof typeof InnodomDateFormats => {
  return key in InnodomDateFormats;
};
const getDateFromString = (raw: string, type: TaxonomyEntryType): [Date, string] => {
  try {
    const chrono = require("chrono-node");
    const date = chrono.strict.parseDate(raw);
    if (date instanceof Date && isDateFormat(type)) {
      return [date, moment(date).format(InnodomDateFormats[type])];
    }
    throw new Error("Unable to extract date value.");
  } catch (err) {
    throw new Error("Unable to extract date value.");
  }
};

const getEnumValueFromString = (raw: string, options: Array<string>): [string, string] => {
  if (options.length < 1 || raw.length < 1) {
    throw new Error("Unable to extract enum value.");
  }
  const cleanRaw = raw.trim().toLowerCase();
  let minIndex = -1;
  let toReturn: null | string = null;
  for (const option of options) {
    const index = cleanRaw.indexOf(option.trim().toLowerCase());
    if (index < 0) {
      continue;
    } else if (minIndex >= 0 && index > minIndex) {
      continue;
    }
    minIndex = index;
    toReturn = option;
  }
  if (toReturn === null) {
    throw new Error("Unable to extract enum value.");
  }
  return [toReturn, toReturn];
};

/**
 * For a given raw string, attempts to extract the semantic value associated with that string.
 * Throws an exception if extracting a semantic value failed, so ensure this function call is
 * always wrapped in a try { } catch.
 * @param raw string The input string.
 * @param entry TaxonomyEntry The taxonomy entry.
 * @return [any, string] Returns the value and the raw value as a string.
 */
export const mapTextToValue = (raw: string, entry: TaxonomyEntry): [any, string] => {
  switch (entry.type) {
    case TaxonomyEntryType.Boolean:
      return getBooleanFromString(raw);
    case TaxonomyEntryType.Number: {
      return getNumberFromString(raw);
    }
    case TaxonomyEntryType.Date:
    case TaxonomyEntryType.DateTime:
    case TaxonomyEntryType.Time: {
      return getDateFromString(raw, entry.type);
    }
    case TaxonomyEntryType.Selection:
      return [raw, raw];
    case TaxonomyEntryType.String:
    case TaxonomyEntryType.Pointer: {
      const trimmed = raw.trim();
      return [trimmed, trimmed];
    }
    case TaxonomyEntryType.Enum:
      return getEnumValueFromString(raw, entry.options);
    case TaxonomyEntryType.Group:
      throw new Error("Cannot set a value for a group.");
  }
  unhandledType(entry);
};

export enum ConfidenceState {
  Empty,
  Manual,
  Low,
  High,
}

export const getConfidenceState = (threshold: number) => (
  dataPoint: DataPointValue<any>,
): ConfidenceState => {
  if (!dataPoint.idRef && !dataPoint.valueRaw) {
    return ConfidenceState.Empty;
  }
  if (dataPoint.confidence >= 1) {
    return ConfidenceState.Manual;
  }
  if (dataPoint.confidence >= threshold) {
    return ConfidenceState.High;
  }
  return ConfidenceState.Low;
};

const dataPointEmpty = (idRef: string | null, value: string): boolean => !idRef && !value;
const insertDataPoint = (
  root: Document,
  meta: Element,
  type: TaxonomyEntryType,
  key: string,
  confidence: number,
  idRef: string | null,
  value: string,
) => {
  const newDataPointTag = root.createElement(TAG_DATAPOINT);
  newDataPointTag.setAttribute(ATTR_KEY, key);
  newDataPointTag.setAttribute(ATTR_CONFIDENCE, confidence.toString(10));
  if (idRef !== null) {
    newDataPointTag.setAttribute(ATTR_ID_REF, idRef);
  }
  if (type !== TaxonomyEntryType.Pointer) {
    newDataPointTag.appendChild(root.createTextNode(value));
  }
  switch (type) {
    case TaxonomyEntryType.Date:
    case TaxonomyEntryType.DateTime:
    case TaxonomyEntryType.Time:
      newDataPointTag.setAttribute("format", InnodomDateFormats[type]);
      break;
    default:
      break;
  }
  meta.appendChild(newDataPointTag);
};

type GroupValues = Array<{
  [dpKey: string]: Array<string>;
}>;
export const extractGroupValues = (
  groupKey: string,
  groupMultiple: boolean,
  taxonomy: State["taxonomy"],
  valueKeys: Array<string>,
): GroupValues => {
  const toReturn: GroupValues = [];
  const keys = valueKeys.filter(k => k.startsWith(`${groupKey.toLocaleLowerCase()}.`));
  const groupEntry = taxonomy.taxonomyObject.find(x => x.id.toLocaleLowerCase() === groupKey.toLocaleLowerCase());

  for (const key of keys) {
    const split = key.toLocaleLowerCase().split(".");
    if (split.length < 2) {
      continue;
    }
    let groupIndex = 0;
    let dataPointIndex = 0;
    let dataPoint: string | null = null;
    if (groupMultiple) {
      if (split.length < 3) {
        continue;
      }
      groupIndex = parseInt(split[1], 10);
      if (isNaN(groupIndex)) {
        continue;
      }
      dataPoint = split[2];

      if (groupEntry.children?.find(x => x.id.toLocaleLowerCase() === dataPoint).multiple) {
        dataPointIndex = parseInt(split[3], 10);
      }

    } else {
      dataPoint = split[1];

      if (groupEntry.children?.find(x => x.id.toLocaleLowerCase() === dataPoint).multiple) {
        dataPointIndex = parseInt(split[2], 10);
      }
    }
    if (toReturn[groupIndex] === undefined) {
      toReturn[groupIndex] = {
        [dataPoint]: [],
      };
    } else if (!(dataPoint in toReturn[groupIndex])) {
      toReturn[groupIndex][dataPoint] = [];
    }
    toReturn[groupIndex][dataPoint][dataPointIndex] = key;
  }
  // the individual group indexes might not actually be sequential
  // for efficiency, the arrays may contain null so we filter them out
  return toReturn
    .filter(r => !!r)
    .map(g => {
      for (const dpKey of Object.keys(g)) {
        g[dpKey] = g[dpKey].filter(v => !!v);
      }
      return g;
    });
};

const fixKeyOrdering = (
  keys: Array<string>,
  taxonomy: WorkbenchTaxonomy,
): any => {
  const toReturn = {};
  for (const taxonomyEntry of taxonomy.taxonomyObject) {
    if (taxonomyEntry.type === TaxonomyEntryType.Group) {
      const groupValues: GroupValues = extractGroupValues(
        taxonomyEntry.id,
        taxonomyEntry.multiple,
        taxonomy,
        keys,
      );
      for (let i = 0; i < groupValues.length; i++) {
        for (const dataPointEntry of taxonomyEntry.children) {
          const dpId : string = dataPointEntry.id.toLocaleLowerCase();
          if (!(dpId in groupValues[i]) || groupValues[i][dpId].length < 1) {
            continue;
          }
          const groupPrefix = taxonomyEntry.multiple
            ? `${taxonomyEntry.id}.${i + 1}`
            : taxonomyEntry.id;
          if (dataPointEntry.multiple) {

            for (let j = 0; j < groupValues[i][dpId].length; j++) {
              toReturn[`${groupPrefix}.${dpId}.${j + 1}`] =
                groupValues[i][dpId][j];
            }
          } else {
            toReturn[`${groupPrefix}.${dpId}`] = groupValues[i][dpId][0];
          }
        }
      }
    } else if (taxonomyEntry.multiple) {
      const relevantKeys = keys.filter(k => k.startsWith(`${taxonomyEntry.id}.`));
      for (let i = 0; i < relevantKeys.length; i++) {
        toReturn[`${taxonomyEntry.id}.${i + 1}`] = relevantKeys[i];
      }
    } else {
      if (keys.includes(taxonomyEntry.id)) {
        toReturn[taxonomyEntry.id] = taxonomyEntry.id;
      }
    }
  }
  return toReturn;
};

export const getOutputXml = (oldXml: string, state: State): string => {
  // parse the XML again but replace the contents
  const parser = new DOMParser();
  const serializer = new XMLSerializer();
  const xmlDoc = parser.parseFromString(oldXml, "text/xml");
  const content = Array.from(xmlDoc.getElementsByTagName(TAG_CONTENT))[0];
  for (const child of Array.from(content.childNodes)) {
    if (!(child instanceof Element)) {
      child.remove();
      continue;
    }
    const el = child as Element;
    const blockId = el.getAttribute(ATTR_ID) || "";
    if (!(blockId in state.blocks)) {
      child.remove();
      continue;
    }
    const block = state.blocks[blockId];
    switch (block.type) {
      case ContentType.Image:
      case ContentType.Math:
      case ContentType.Text:
      case ContentType.Table:
        el.innerHTML = "";
        el.appendChild(block.element.cloneNode(true) as Element);
        break;
      default:
        // for table cells, we insert them into the table instead
        break;
    }
  }
  stripNamespaceAttributes(xmlDoc.documentElement);
  const metaTag = Array.from(xmlDoc.getElementsByTagName(TAG_META))[0];
  metaTag.innerHTML = "";
  // remove empty values
  const nonEmptyValueKey = Object.keys(state.values).filter(
    k => !dataPointEmpty(state.values[k].idRef, state.values[k].valueRaw),
  );

  const sortedValueKeys = sortValueKeys(nonEmptyValueKey, state.taxonomy);
  
  // It's possible that the user adds multiple data points and leaves earlier ones 
  // unpopulated.  If we just translate as is, this would result in datapoint.2 appearing
  // without any datapoint.1 preceding it in the xml file (bad!)
  // So we can basically renumber the values so that they'll start from 1 and be sequential.
  const reorderedKeys = fixKeyOrdering(
    sortedValueKeys,
    state.taxonomy
  );
  
  for (const reorderedKey of Object.keys(reorderedKeys)) {
    // If a point was reordered, for instance point.5 was alone 
    // and became point.1 in reoderedKeys, we have to fetch the value using
    // the original key.  So it's a double lookup point.1 -> point.5 -> values[point.5]
    let referencedKey = reorderedKeys[reorderedKey]

    const value = state.values[referencedKey];
    let texoEntry = extractKeysAndIndexes(value.valueKey, state.taxonomy.taxonomyObject).taxonomyEntry;

    insertDataPoint(
      xmlDoc,
      metaTag,
      texoEntry.type,
      reorderedKey,
      value.confidence,
      value.idRef,
      value.valueRaw,
    );
  }
  // for (let valueKey of Object.keys(state.values)) {
  //   let value = state.values[valueKey];
  //   let texoEntry = extractKeysAndIndexes(valueKey, state.taxonomy.taxonomyObject).taxonomyEntry;
  //   insertDataPoint(
  //     xmlDoc,
  //     metaTag,
  //     texoEntry.type,
  //     valueKey,
  //     value.confidence,
  //     value.idRef,
  //     value.valueRaw);
  // }
  
  return serializer.serializeToString(xmlDoc);
};

export const sortValueKeys = (
  keys: Array<string>,
  taxonomy: State["taxonomy"],
): Array<string> => {
  return keys.sort((k1: string, k2: string): number => {
    const split1 = k1.split(".");
    const split2 = k2.split(".");
    let compareIndex = 0;
    if (split1[compareIndex] !== split2[compareIndex]) {
      return (
        taxonomy.taxonomyObject.findIndex(x => x.id.toLocaleLowerCase() === split1[compareIndex].toLocaleLowerCase()) -
        taxonomy.taxonomyObject.findIndex(x => x.id.toLocaleLowerCase() === split2[compareIndex].toLocaleLowerCase())
      );
    }
    const taxonomyEntry = taxonomy.taxonomyObject.find(x => x.id === split1[compareIndex]);
    if (taxonomyEntry.multiple) {
      compareIndex++;
      const multipleOrder = parseInt(split1[compareIndex], 10) - parseInt(split2[compareIndex], 10);
      if (multipleOrder !== 0) {
        return multipleOrder;
      }
    }
    compareIndex++;
    if (taxonomyEntry.type === TaxonomyEntryType.Group) {
      if (split1[compareIndex] !== split2[compareIndex]) {
        return (
          taxonomyEntry.children.findIndex(x => x.id.toLocaleLowerCase() === split1[compareIndex].toLocaleLowerCase()) -
          taxonomyEntry.children.findIndex(x => x.id.toLocaleLowerCase() ===split2[compareIndex].toLocaleLowerCase())
        );
      }
      const dataPointEntry = taxonomyEntry.children.find(x => x.id.toLocaleLowerCase() === split1[compareIndex].toLocaleLowerCase());
      if (dataPointEntry.multiple) {
        compareIndex++;
        return parseInt(split1[compareIndex], 10) - parseInt(split2[compareIndex], 10);
      }
    }
    return 0;
  });
};

export const convertToArrayTaxo = (taxonomy: any) => {
  let finalEntries: any[] = [];
  for (let key of Object.keys(taxonomy)) {
    
    let taxoEntry = {
      id: key.toLocaleLowerCase(),
      ...taxonomy[key],
    };
    if (taxoEntry.type === "group") {
      taxoEntry.children = [];
    }

    if (taxoEntry.groups) {
      // Skip until we know we've created all the group nodes
      continue;
    } 
    // remove some obsolete fields
    delete taxoEntry.requiresSupport;
    finalEntries.push(taxoEntry);
  }
  for (let key of Object.keys(taxonomy)) {
    let taxoEntry = {...taxonomy[key]};
    if (taxoEntry.groups && taxoEntry.groups.length > 0) {
      taxoEntry.id = key.toLocaleLowerCase();
      taxoEntry.groups.forEach(group => {
        finalEntries.find(x => x.id === group).children.push({...taxoEntry})
      }) 
      // remove some obsolete fields
      delete taxoEntry.groups;
      delete taxoEntry.requiresSupport;
    } 
  }
  return finalEntries;
}